import React from 'react'
import PropTypes from 'prop-types'
import { Elements, injectStripe, StripeProvider } from 'react-stripe-elements'
import { Button, Modal } from 'semantic-ui-react'

import UnsavedChangesGuard from 'shared/components/UnsavedChangesGuard'
import { getPublishableKey, isUsBilling } from 'libs/billing/stripe-account'
import toast from 'libs/toast'
import { PaymentForm } from 'components/PaymentForm'
import useFeatureToggle from 'hooks/useFeatureToggle'
import useAxios, { mutate } from 'hooks/useAxios'

class PaymentModal extends React.Component {
  static propTypes = {
    label: PropTypes.string.isRequired,
    stripe: PropTypes.object,
    variant: PropTypes.oneOf(['cardElement', 'paymentElement']),
    updateCardDirect: PropTypes.func,
  }

  state = {
    name: '',
    address: '',
    city: '',
    province: '',
    zip: '',
    isModalOpened: false,
    isCardCompleted: false,
    // Tracks Stripe's `event.empty` signal. Starts true (card fields are blank
    // on mount). Transitions to false when the user types ANY character in the
    // card or address fields — the correct dirty signal for a half-typed invalid
    // card (empty=false, complete=false) that `isCardCompleted` misses.
    isCardEmpty: true,
    loading: false,
    // Incremented whenever we need to force-remount PaymentForm — used
    // for the paymentElement variant's retry-after-BE-error path.
    // Stripe's SetupIntent client_secret is single-use, so once
    // confirmSetup has succeeded (which happens BEFORE the BE POST
    // fires), the client_secret is spent. If the subsequent BE POST
    // fails, the user's next Save click would otherwise reuse the
    // spent secret and hit `setup_intent_unexpected_state`. Remounting
    // PaymentForm fetches a fresh SetupIntent.
    paymentElementRemountKey: 0,
  }

  hiddenSubmitButtonRef = React.createRef()

  onTextChange = (event) => {
    const { name, value } = event.target
    this.setState({ [name]: value })
  }

  onCardChange = (event) => {
    const update = {}
    if (typeof event.complete !== 'undefined') {
      update.isCardCompleted = event.complete
    }
    if (typeof event.empty !== 'undefined') {
      update.isCardEmpty = event.empty
    }
    this.setState(update)
  }

  onModalOpened = (event) => {
    // The trigger is an <a href="#">. Without preventDefault the browser also
    // processes the hash navigation, and when the sibling billing-details form
    // is dirty its page-level route guard (<Prompt>) intercepts that as
    // "leaving with unsaved changes" — firing a spurious confirm on modal OPEN.
    // Opening a same-page modal is not navigating away, so suppress the nav.
    if (event) {
      event.preventDefault()
    }
    this.setState({ isModalOpened: true })
  }

  onModalClosed = () => {
    const { name, address, city, province, zip, isCardEmpty } = this.state
    const hasOwnFieldChanged =
      name !== '' || address !== '' || city !== '' || province !== '' || zip !== ''
    const isDirty = !isCardEmpty || hasOwnFieldChanged
    if (isDirty) {
      const confirmed = window.confirm(
        'Are you sure you want to leave? Changes you made may not be saved.'
      )
      if (!confirmed) {
        return
      }
    }
    this.setState({
      name: '',
      address: '',
      city: '',
      province: '',
      zip: '',
      isModalOpened: false,
      isCardCompleted: false,
      isCardEmpty: true,
      loading: false,
    })
  }

  // Legacy cardElement variant — used by <Form onSubmit> in that path.
  onSubmit = (event) => {
    event.preventDefault()
    this.setState({ loading: true })
    this.props.stripe
      .createToken({
        name: this.state.name,
        address_line1: this.state.address,
        address_city: this.state.city,
        address_state: this.state.province,
        address_zip: this.state.zip,
      })
      .then((response) =>
        this.props
          .updateCardDirect({ data: response.token || {} })
          .then(this.onCardTokenSaved)
          .catch(this.onCardTokenSaveFailed)
      )
  }

  onCardTokenSaved = () => {
    this.setState({
      name: '',
      address: '',
      city: '',
      province: '',
      zip: '',
      isModalOpened: false,
      isCardCompleted: false,
      isCardEmpty: true,
      loading: false,
    })
    toast.success('Your payment info was successfully updated.')
  }

  onCardTokenSaveFailed = () => {
    this.setState({ loading: false })
    toast.error('Your payment info failed to update. Please try again.')
  }

  // paymentElement variant callback — PaymentFormPaymentElement runs its
  // own confirmSetup internally and hands back a `pm_...` id via onSaved.
  //
  // Direct promise handling: close modal + toast on success (matches
  // the app-wide close-on-success pattern), bump remount key + toast
  // on failure so the user can retry with a fresh SetupIntent.
  onPaymentMethodSaved = (paymentMethodId) => {
    this.props
      .updateCardDirect({ data: { id: paymentMethodId } })
      .then(() => {
        this.setState({
          name: '',
          address: '',
          city: '',
          province: '',
          zip: '',
          isModalOpened: false,
          isCardCompleted: false,
          isCardEmpty: true,
          loading: false,
        })
        toast.success('Your payment info was successfully updated.')
        mutate('billing/cards/')
        mutate('billing/')
      })
      .catch(() => {
        // Remounting PaymentForm fetches a fresh SetupIntent. The remounted
        // card is empty by definition — reset isCardEmpty so the dirty guard
        // doesn't fire on the next close attempt before the user re-enters data.
        this.setState((prevState) => ({
          loading: false,
          isCardCompleted: false,
          isCardEmpty: true,
          paymentElementRemountKey: prevState.paymentElementRemountKey + 1,
        }))
        toast.error('Your payment info failed to update. Please try again.')
      })
  }

  onPaymentMethodError = (message) => {
    this.setState({ loading: false })
    toast.error(message)
  }

  onSaveClick = () => {
    // Immediately show the loading state — for the paymentElement variant,
    // confirmSetup takes ~1-2s and the user needs feedback. For cardElement
    // this is redundant with onSubmit's setState, but harmless.
    this.setState({ loading: true })
    const input = this.hiddenSubmitButtonRef.current
    if (input && input.form) {
      // requestSubmit() dispatches the actual submit event (unlike
      // .submit() which skips it, and unlike .click() on a hidden submit
      // input which is unreliable inside PaymentElement's iframe tree).
      input.form.requestSubmit(input)
    }
  }

  render() {
    const loading = this.state.loading
    const { name, address, city, province, zip, isCardEmpty } = this.state
    const hasOwnFieldChanged =
      name !== '' || address !== '' || city !== '' || province !== '' || zip !== ''
    const isDirty = !isCardEmpty || hasOwnFieldChanged
    return (
      <React.Fragment>
        <UnsavedChangesGuard when={isDirty} guardRefresh={false} />
        <Modal
          trigger={
            <a href="#" onClick={this.onModalOpened}>
              {this.props.label}
            </a>
          }
          open={this.state.isModalOpened}
          onClose={this.onModalClosed}
          size="small"
        >
          <Modal.Header>Update your payment info</Modal.Header>
          <Modal.Content>
            <PaymentForm
              key={this.state.paymentElementRemountKey}
              variant={this.props.variant}
              // cardElement props (ignored by paymentElement)
              name={this.state.name}
              address={this.state.address}
              city={this.state.city}
              province={this.state.province}
              zip={this.state.zip}
              onCardChange={this.onCardChange}
              onSubmit={this.onSubmit}
              onTextChange={this.onTextChange}
              // paymentElement props (ignored by cardElement)
              onSaved={this.onPaymentMethodSaved}
              onError={this.onPaymentMethodError}
              forwardedRef={this.hiddenSubmitButtonRef}
            />
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.onModalClosed}>Cancel</Button>
            <Button
              primary
              type="submit"
              loading={loading}
              disabled={!this.state.isCardCompleted || loading}
              onClick={this.onSaveClick}
            >
              Update card
            </Button>
          </Modal.Actions>
        </Modal>
      </React.Fragment>
    )
  }
}

const EnhancedPaymentModal = injectStripe(PaymentModal)

export const UpdatePaymentModal = ({ label, billing }) => {
  const { enableBillingTaxId } = useFeatureToggle()
  const publishableKey = getPublishableKey(billing)
  const useModernForm = enableBillingTaxId || isUsBilling(billing)
  const { fetch: updateCardDirect } = useAxios({
    url: 'billing/cards/',
    method: 'post',
    skip: true,
  })
  return (
    <StripeProvider key={publishableKey} apiKey={publishableKey}>
      <Elements>
        <EnhancedPaymentModal
          label={label}
          variant={useModernForm ? 'paymentElement' : 'cardElement'}
          updateCardDirect={updateCardDirect}
        />
      </Elements>
    </StripeProvider>
  )
}

UpdatePaymentModal.propTypes = {
  label: PropTypes.string,
  billing: PropTypes.object,
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/Billing/updatePaymentModal.js